import React from "react";
import { Download, FileImage, FileText, Loader2 } from "lucide-react";
import { exportToPNG, exportToPDF, generateFilename } from "../../utils/exportUtils";
import Button from "../ui/Button";
import { useDispatch } from "react-redux";
import { addToHistory } from "../../app/features/tweets";

const ExportButtons = ({ tweetData, previewSettings, exportFormat, onExportStart, onExportComplete }) => {
  const dispatch = useDispatch();
  const [isExporting, setIsExporting] = React.useState(false);
  const [currentFormat, setCurrentFormat] = React.useState(null);

  const handleExport = async (format) => {
    if (!tweetData) {
      alert("No tweet to export. Please fetch a tweet first.");
      return;
    }

    const element = document.getElementById("tweet-preview-export");
    if (!element) {
      alert("Preview element not found. Please try again.");
      return;
    }

    try {
      setIsExporting(true);
      setCurrentFormat(format);
      onExportStart?.();

      // Ensure element is visible and has dimensions
      const originalStyle = {
        visibility: element.style.visibility,
        opacity: element.style.opacity,
        display: element.style.display,
      };
      
      // Make sure element is visible
      element.style.visibility = 'visible';
      element.style.opacity = '1';
      element.style.display = 'block';

      const filename = generateFilename(tweetData, format);

      if (format === "png") {
        await exportToPNG(element, filename, 2);
      } else if (format === "pdf") {
        await exportToPDF(element, filename);
      }

      // Restore original styles
      element.style.visibility = originalStyle.visibility || '';
      element.style.opacity = originalStyle.opacity || '';
      element.style.display = originalStyle.display || '';

      // Add to history
      dispatch(
        addToHistory({
          tweetId: tweetData.id,
          url: tweetData.url,
          format,
          filename,
          timestamp: new Date().toISOString(),
        })
      );

      onExportComplete?.();
    } catch (error) {
      console.error("Export error:", error);
      alert(`Failed to export: ${error.message}`);
    } finally {
      setIsExporting(false);
      setCurrentFormat(null);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Download className="w-5 h-5" />
        Export Options
      </h3>

      <div className="grid grid-cols-2 gap-3">
        <Button
          onClick={() => handleExport("png")}
          disabled={!tweetData || isExporting}
          className="w-full"
          variant="default"
        >
          {isExporting && currentFormat === "png" ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileImage className="w-4 h-4 mr-2" />
          )}
          PNG
        </Button>

        <Button
          onClick={() => handleExport("pdf")}
          disabled={!tweetData || isExporting}
          className="w-full"
          variant="default"
        >
          {isExporting && currentFormat === "pdf" ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-2" />
          )}
          PDF
        </Button>
      </div>

      {!tweetData && (
        <p className="text-xs text-zinc-500 text-center mt-2">
          Fetch a tweet to enable export
        </p>
      )}
    </div>
  );
};

export default ExportButtons;

